import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import { CustomButton } from "./customButton";
import type { ApiResponse } from "../../services/genesisApi/httpsMethods";

interface ErrorDialogProps {
  open: boolean;
  title?: string;
  closeText?: string;
  onClose: () => void;
  apiResponse?: ApiResponse<any>;
  customMessage?: string;
}

export const ErrorDialog = ({
  open,
  title = "Error",
  closeText = "Close",
  onClose,
  apiResponse,
  customMessage,
}: ErrorDialogProps) => {
  const getErrorMessage = (): string => {
    if (customMessage) {
      return customMessage;
    }

    if (apiResponse) {
      if (apiResponse.message != null) {
        const m = apiResponse.message;
        return typeof m === "string" ? m : m.message;
      }

      if (!apiResponse.success) {
        const extra = apiResponse as ApiResponse & { statusText?: string };
        const statusText = extra.statusText ? ` ${extra.statusText}` : "";
        return `Request failed with status ${apiResponse.status}${statusText}`;
      }
    }

    return "An unexpected error occurred.";
  };

  const getErrorTitle = (): string => {
    if (apiResponse && apiResponse.status) {
      switch (apiResponse.status) {
        case 400:
          return "Bad Request (400)";
        case 401:
          return "Unauthorized (401)";
        case 403:
          return "Forbidden (403)";
        case 404:
          return "Not Found (404)";
        case 409:
          return "Conflict (409)";
        case 422:
          return "Validation Error (422)";
        case 500:
          return "Server Error (500)";
        default:
          return `${title} (${apiResponse.status})`;
      }
    }
    return title;
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{getErrorTitle()}</DialogTitle>
      <DialogContent>
        <DialogContentText>{getErrorMessage()}</DialogContentText>
        {apiResponse && apiResponse.status && (
          <DialogContentText
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, fontSize: "0.875rem" }}
          >
            Status Code: {apiResponse.status}
            {(apiResponse as any).statusText &&
              ` (${(apiResponse as any).statusText})`}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          handleButtonPress={onClose}
          isButtonDisabled={false}
          text={closeText}
          solid={false}
        />
      </DialogActions>
    </Dialog>
  );
};
